import React, { Fragment, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import "./Banner.less";

import { ZomentumRoutes } from "@/Contracts";
import { BannerNotificationMessageType } from "@/Contracts/Others/NotificationFeed";
import { ActivityMessageType } from "@/Contracts/Others/RecentActivityFeed";

import BannerNotificationActionCreators from "@/Actions/BannerNotification";
import { isDocumentClientView } from "@/Contracts/Utils";
import { useLocation, useRouteMatch, useHistory } from "react-router-dom";
import { GlobalState } from "@/Reducers/interface";
import { ZTypography, ZIcon } from "@atoms";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";

const Banner = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const history = useHistory();
	const { isOpen, isLoading, bannerFeedMessage } = useSelector(
		(state: GlobalState) => state.bannerNotification
	);

	const isEndClientView = isDocumentClientView(location.pathname);
	const isPdfView = useRouteMatch(ZomentumRoutes.DocumentsPdfView)?.isExact;

	const bannerMutationObserver = useRef<MutationObserver | null>();

	const getDOMElements = (): Record<string, HTMLElement> => {
		const rightDrawer = document.getElementsByClassName(
			"ant-drawer-right"
		)[0] as HTMLElement;

		const bannerWrap = document.getElementsByClassName(
			"banner-card-wrap"
		)[0] as HTMLElement;

		return {
			bannerWrap,
			rightDrawer
		};
	};

	const adjustDrawerOffset = (
		drawer: HTMLElement | undefined,
		withBanner: boolean
	) => {
		if (!drawer) return;
		drawer.style.top = withBanner ? "40px" : "0px";
		drawer.style.height = withBanner ? "calc(100% - 40px)" : "100%";
	};

	const observeAddedNodes = (addedNodes: NodeList) => {
		addedNodes.forEach(node => {
			const addedNode = node as HTMLElement;
			if (!!!addedNode.tagName) return;

			const { bannerWrap } = getDOMElements();
			const isNotificationBannerPresent =
				bannerWrap?.classList?.contains?.("show");

			// Adjust right drawers if the notification banner is being shown.
			const isDrawerOpened =
				addedNode?.classList?.contains?.("ant-drawer-right");

			if (isNotificationBannerPresent && isDrawerOpened) {
				adjustDrawerOffset(addedNode, true);
			}
		});
	};

	const mutationsObserverCallback = (mutations: MutationRecord[]) => {
		mutations.forEach(mutation => {
			observeAddedNodes(mutation.addedNodes);
		});
	};

	useEffect(() => {
		bannerMutationObserver.current = new MutationObserver(
			mutationsObserverCallback
		);

		bannerMutationObserver.current.observe(document.body, {
			childList: true,
			subtree: true
		});

		return () => {
			bannerMutationObserver.current?.disconnect();
		};
	}, []);

	useEffect(() => {
		const { rightDrawer } = getDOMElements();
		const shouldOffsetDrawer = isOpen && !isEndClientView && !isPdfView;
		adjustDrawerOffset(rightDrawer, shouldOffsetDrawer);
	}, [isOpen, isEndClientView, isPdfView]);

	if (isEndClientView || isPdfView) {
		return null;
	}

	const severity =
		(bannerFeedMessage && bannerFeedMessage.banner_severity) || "";

	const dismissNotification = async () => {
		const { rightDrawer } = getDOMElements();

		adjustDrawerOffset(rightDrawer, false);

		dispatch(BannerNotificationActionCreators.dismissBanner());
	};

	const handleNotificationLinkClick = (
		messageType: BannerNotificationMessageType | ActivityMessageType,
		value?: string
	) => {
		if (messageType === ActivityMessageType.REDIRECT && !!value?.length) {
			history.replace(value);
		}
	};

	return (
		<div className={`banner-card-wrap${isOpen ? " show" : ""}`}>
			<div className={`banner-card ${severity}`}>
				{!isLoading ? (
					<Fragment>
						{!!bannerFeedMessage?.message?.length && (
							<ZIcon
								color={EColors.GOLD}
								colorVariant={6}
								name={EIcons.WARNINGCIRCLE}
								size="sm"
							/>
						)}
						{bannerFeedMessage?.message?.map(
							({ message_type, text, value }, index) => {
								return message_type ===
									BannerNotificationMessageType.TEXT ? (
									<ZTypography.ZText
										weight={EFontWeights.MEDIUM}
										key={`banner-text-${index}`}
										className="text-1"
									>
										{text}
									</ZTypography.ZText>
								) : (
									<ZTypography.ZLink
										color={EColors.GEEKBLUE}
										colorVariant={6}
										weight={EFontWeights.MEDIUM}
										className="text-1"
										onClick={() =>
											handleNotificationLinkClick(
												message_type,
												value
											)
										}
										key={`banner-text-link-${index}`}
									>
										{text}
									</ZTypography.ZLink>
								);
							}
						)}
						{bannerFeedMessage?.is_dismissable &&
							!!bannerFeedMessage?.message?.length && (
								<ZTypography.ZText
									weight={EFontWeights.MEDIUM}
									onClick={dismissNotification}
									className="close-icon text-1"
								>
									Remind Later
								</ZTypography.ZText>
							)}
					</Fragment>
				) : (
					<span>Loading...</span>
				)}
			</div>
		</div>
	);
};

export default Banner;
